import { pageRouteTemplates } from '@/__generated__/routeTemplates';

interface RouteNode {
  isPage: boolean;
  statics: Map<string, RouteNode>;
  dynamic?: RouteNode;
  /** Set when the node has a catch-all page (`[...slug]` or `[[...slug]]`). */
  catchAll?: { isOptional: boolean };
}

const routeTree = buildRouteTree(pageRouteTemplates);

/**
 * Returns the route of `url` with the values of the dynamic segments of the matching page in `src/app/[locale]`
 * replaced by `[id]` (e.g. `/ja-JP/courses/[id]` for `/ja-JP/courses/kawahara_java_basic_2_2026`), so that
 * KaizenSpring groups the page views of slug routes by route. Like Next.js, a static segment takes precedence over a
 * dynamic one. A catch-all page keeps its whole path, since its paths name distinct articles, and a path matching no
 * page is returned as is.
 */
export function toRouteTemplate(url: URL): string {
  const [, locale, ...segments] = url.pathname.split('/');
  const matched = matchRoute(routeTree, segments);
  return matched ? ['', locale, ...matched].join('/') : url.pathname;
}

function matchRoute(node: RouteNode, segments: readonly string[]): string[] | undefined {
  const [segment, ...rest] = segments;
  if (segment === undefined || (segment === '' && rest.length === 0)) {
    if (node.isPage || node.catchAll?.isOptional) return segment === undefined ? [] : [''];
    return undefined;
  }
  const staticChild = node.statics.get(segment);
  const staticMatch = staticChild && matchRoute(staticChild, rest);
  if (staticMatch) return [segment, ...staticMatch];
  const dynamicMatch = node.dynamic && matchRoute(node.dynamic, rest);
  if (dynamicMatch) return ['[id]', ...dynamicMatch];
  return node.catchAll ? [...segments] : undefined;
}

function buildRouteTree(templates: readonly string[]): RouteNode {
  const root = createRouteNode();
  for (const template of templates) {
    let node = root;
    for (const segment of template.split('/').filter(Boolean)) {
      if (segment.startsWith('[[...') || segment.startsWith('[...')) {
        node.catchAll = { isOptional: segment.startsWith('[[...') };
        break;
      }
      node = getOrCreateChild(node, segment);
    }
    if (!template.includes('...')) node.isPage = true;
  }
  return root;
}

function getOrCreateChild(node: RouteNode, segment: string): RouteNode {
  if (segment.startsWith('[')) {
    node.dynamic ??= createRouteNode();
    return node.dynamic;
  }
  let child = node.statics.get(segment);
  if (!child) {
    child = createRouteNode();
    node.statics.set(segment, child);
  }
  return child;
}

function createRouteNode(): RouteNode {
  return { isPage: false, statics: new Map() };
}
